import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, Checkbox, FormControl, FormLabel, HStack, Input, NumberInput,
    NumberInputField, Radio, RadioGroup, Select, SimpleGrid, Text, VStack, Button, Alert, AlertIcon,
} from '@chakra-ui/react';
import { AppDialog, DialogButton } from '../common/AppDialog';
import { pickFileToSave, writeHandleBinary, downloadBinaryFile } from '../../services/fileSystem/fileAccess';
import { useDocumentStore } from '../../state/useDocumentStore';
import { useLayoutStore } from '../../state/useLayoutStore';
import { useSheetSetStore } from '../../state/sheetSetStore';
import { PAPER_SIZES } from '../../state/useLayoutStore';
import {
    applyDefaultPageSetup, layoutPageSetup, naturalPaperId, patchPageSetup,
    STANDARD_SCALES, paperSizeMm,
} from '../../services/plot/pageSetup';
import { buildPlotPage, stampScaleLabel } from '../../services/plot/plotJob';
import { exportPlotPages } from '../../services/plot/pdfExporter';
import { grayscaleTable, loadCtbBlob, monochromeTable } from '../../services/plot/ctb';
import type { PlotDialogRequest } from '../../services/plot/plotDialogBridge';
import type { PageSetup, PlotStyleTable } from '../../services/plot/types';
import { notify } from '../../utils/notify';
import i18n from '../../i18n';

const PDF_FILE_TYPES = [
    { description: 'PDF Document', accept: { 'application/pdf': ['.pdf'] } },
];

const BUILT_IN_STYLES = ['', 'monochrome.ctb', 'grayscale.ctb'];

interface PlotDialogProps {
    isOpen: boolean;
    request: PlotDialogRequest | null;
    onClose: () => void;
}

/**
 * PLOT dialog (Faz 2 §3.4): plot area, paper + orientation, scale
 * (fit or standard/custom), offset, CTB plot styles (built-in monochrome /
 * grayscale + imported .ctb files), option switches and the OS save flow.
 * PUBLISH mode plots the selected layouts into one PDF, each with its own
 * saved page setup.
 */
export function PlotDialog({ isOpen, request, onClose }: PlotDialogProps) {
    const { t } = useTranslation();
    const publishMode = request?.mode === 'publish';

    const layouts = useLayoutStore((s) => s.layouts);
    const setPageSetup = useLayoutStore((s) => s.setPageSetup);
    const documentName = useDocumentStore((s) => s.displayName);
    const hasSheetSet = useSheetSetStore((s) => !!s.set);

    const [layoutId, setLayoutId] = useState<string | null>(null);
    const [publishIds, setPublishIds] = useState<string[]>([]);
    const [publishSource, setPublishSource] = useState<'layouts' | 'sheetSet'>('layouts');
    const [setup, setSetup] = useState<PageSetup>(() => applyDefaultPageSetup());
    const [fileName, setFileName] = useState('plot');
    const [customStyles, setCustomStyles] = useState<{ name: string; table: PlotStyleTable }[]>([]);
    const [styleWarnings, setStyleWarnings] = useState<string[]>([]);
    const [saveToLayout, setSaveToLayout] = useState(true);
    const [busy, setBusy] = useState(false);

    // Open-time initialization: layout setups prefill, model plots default
    // to extents-fit on the default sheet.
    useEffect(() => {
        if (!isOpen) return;
        const store = useLayoutStore.getState();
        const lid = request?.layoutId ?? store.activeId;
        setLayoutId(lid);
        const layout = store.layouts.find((l) => l.id === lid);
        // A layout always plots itself (area select is pinned); model plots
        // default to extents-fit on the default sheet.
        setSetup(layout
            ? { ...layoutPageSetup(layout), plotType: 'layout' }
            : { ...applyDefaultPageSetup(), plotType: 'extents', fitToPaper: true });
        setFileName(documentName ? `${documentName}-plot` : 'plot');
        setSaveToLayout(!!layout);
        setStyleWarnings([]);
        setPublishIds(store.layouts.map((l) => l.id));
        // Faz 10.13: "from Sheet Set" source only when the document has one.
        const ss = useSheetSetStore.getState().set;
        setPublishSource(request?.source === 'sheetSet' && !!ss ? 'sheetSet' : 'layouts');
        if (ss && request?.source === 'sheetSet') setFileName(documentName ? `${documentName}-publish` : 'publish');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, request]);

    const patch = (p: Partial<PageSetup>) => setSetup((s) => patchPageSetup(s, p));

    const activeLayout = useMemo(
        () => layouts.find((l) => l.id === layoutId) ?? null,
        [layouts, layoutId],
    );

    // Sheet-set publish targets: Include-for-Publish sheets in set order,
    // resolving to layouts that still exist (stale bindings drop silently —
    // the palette marks them with a warning icon instead).
    const publishSheets = useMemo(() => {
        const ss = useSheetSetStore.getState().set;
        if (!ss) return [] as { layoutId: string; title: string; number: string }[];
        const out: { layoutId: string; title: string; number: string }[] = [];
        for (const sub of ss.subsets) {
            for (const sheet of sub.sheets) {
                if (sheet.includeForPublish && sheet.layoutId && layouts.some((l) => l.id === sheet.layoutId)) {
                    out.push({ layoutId: sheet.layoutId, title: sheet.title, number: sheet.number });
                }
            }
        }
        return out;
    }, [layouts, hasSheetSet]);

    const importCtb = async (file: File) => {
        try {
            const table = await loadCtbBlob(new Uint8Array(await file.arrayBuffer()), file.name);
            setCustomStyles((list) => [
                ...list.filter((s) => s.name !== file.name),
                { name: file.name, table },
            ]);
            patch({ plotStyleName: file.name });
            setStyleWarnings(table.loadWarnings);
            if (table.isStb) {
                notify(i18n.t('tools.notify.stbParsedButNotSupported', { name: file.name }), 'warning');
            }
        } catch (e) {
            console.error('[PlotDialog] CTB import failed:', e);
            notify(i18n.t('tools.notify.ctbImportFailed', { name: file.name }), 'error');
        }
    };

    const styleTableFor = async (name: string): Promise<PlotStyleTable | undefined> => {
        if (name === 'monochrome.ctb') return monochromeTable();
        if (name === 'grayscale.ctb') return grayscaleTable();
        return customStyles.find((s) => s.name === name)?.table;
    };

    const runExport = async () => {
        setBusy(true);
        try {
            const fromSheetSet = publishMode && publishSource === 'sheetSet';
            const publishTargetIds = fromSheetSet
                ? publishSheets.map((s) => s.layoutId)
                : publishIds;
            const tables = new Map<string, PlotStyleTable>();
            for (const name of new Set(publishMode
                ? publishTargetIds.map((id) => layoutPageSetup(layouts.find((l) => l.id === id)).plotStyleName)
                : [setup.plotStyleName])) {
                if (!name) continue;
                const table = await styleTableFor(name);
                if (table) tables.set(name, table);
            }

            let pages;
            let stampScale: string;
            if (publishMode) {
                const targets = (fromSheetSet
                    ? publishSheets.map((s) => ({
                        id: s.layoutId,
                        title: s.number ? `${s.number} — ${s.title}` : s.title,
                    }))
                    : layouts
                        .filter((l) => publishIds.includes(l.id))
                        .map((l) => ({ id: l.id, title: l.name })));
                if (targets.length === 0) {
                    notify(i18n.t(fromSheetSet
                        ? 'tools.notify.publishNoSheetsIncluded'
                        : 'tools.notify.publishNoLayoutsSelected'), 'warning');
                    setBusy(false);
                    return;
                }
                pages = targets.map((target) => {
                    const layout = layouts.find((l) => l.id === target.id);
                    if (!layout) return null;
                    const saved = layoutPageSetup(layout);
                    return buildPlotPage({
                        plotType: 'layout', layoutId: layout.id, setup: saved,
                        options: saved.options, title: target.title,
                    });
                }).filter((p): p is NonNullable<typeof p> => p !== null);
                stampScale = 'PUBLISH';
            } else {
                if (saveToLayout && layoutId) setPageSetup(layoutId, setup);
                pages = [buildPlotPage({
                    plotType: setup.plotType,
                    layoutId,
                    window: setup.plotWindow,
                    setup,
                    options: setup.options,
                    title: activeLayout?.name ?? 'Model',
                })];
                stampScale = stampScaleLabel(setup);
            }

            const result = await exportPlotPages(pages, tables, {
                documentName: documentName || 'drawing',
                stampScale,
            });
            if (result.warnings.length > 0) setStyleWarnings(result.warnings);

            const safeName = (fileName.trim() || 'plot').replace(/\.pdf$/i, '');
            const full = `${safeName}.pdf`;
            const handle = await pickFileToSave(full, PDF_FILE_TYPES);
            if (handle) {
                await writeHandleBinary(handle, result.bytes);
                notify(i18n.t('tools.notify.pdfPlotted', { fileName: handle.name }), 'success');
            } else {
                downloadBinaryFile(full, result.bytes);
                notify(i18n.t('tools.notify.pdfPlotted', { fileName: full }), 'success');
            }
            onClose();
        } catch (e) {
            console.error('[PlotDialog] plot failed:', e);
            notify(i18n.t('tools.notify.plotFailed'), 'error');
        } finally {
            setBusy(false);
        }
    };

    const paperId = naturalPaperId(setup.paperWidth, setup.paperHeight) ?? 'A4';
    const styleNames = [...BUILT_IN_STYLES, ...customStyles.map((s) => s.name)];

    return (
        <AppDialog
            isOpen={isOpen}
            onClose={onClose}
            size="lg"
            title={publishMode ? t('dialogs.ui.plot.publishTitle') : t('dialogs.ui.plot.title')}
            footer={
                <>
                    <DialogButton variant="ghost" onClick={onClose}>{t('dialogs.ui.cancel')}</DialogButton>
                    <DialogButton
                        variant="primary"
                        isDisabled={busy
                            || (publishMode && (publishSource === 'sheetSet'
                                ? publishSheets.length === 0
                                : publishIds.length === 0))}
                        onClick={() => void runExport()}
                    >
                        {t('dialogs.ui.plot.plotAndSave')}
                    </DialogButton>
                </>
            }
        >
            <VStack align="stretch" spacing={3}>
                {publishMode ? (
                    <>
                        <FormControl>
                            <FormLabel fontSize="xs">{t('dialogs.ui.plot.layouts')}</FormLabel>
                            <RadioGroup
                                value={publishSource}
                                onChange={(value: string) => setPublishSource(value as 'layouts' | 'sheetSet')}
                            >
                                <HStack spacing={4} mb={1}>
                                    <Radio value="layouts" size="sm">
                                        <Text fontSize="xs">{t('dialogs.ui.plot.sourceLayouts')}</Text>
                                    </Radio>
                                    <Radio value="sheetSet" size="sm" isDisabled={!hasSheetSet}>
                                        <Text fontSize="xs">{t('dialogs.ui.plot.sourceSheetSet')}</Text>
                                    </Radio>
                                </HStack>
                            </RadioGroup>
                            {publishSource === 'sheetSet' ? (
                                <VStack align="stretch" spacing={1} maxH="180px" overflowY="auto" borderWidth="1px" borderColor="border.base" borderRadius="md" p={2}>
                                    {publishSheets.length === 0 && (
                                        <Text fontSize="xs" color="text.secondary">{t('dialogs.ui.plot.sheetSetEmpty')}</Text>
                                    )}
                                    {publishSheets.map((s, i) => (
                                        <Text key={`${s.layoutId}-${s.number}-${i}`} fontSize="xs">
                                            {s.number ? `${s.number} — ${s.title}` : s.title}
                                        </Text>
                                    ))}
                                </VStack>
                            ) : (
                                <>
                                    <HStack spacing={3} mb={1}>
                                        <Checkbox
                                            size="sm"
                                            isChecked={publishIds.length === layouts.length && layouts.length > 0}
                                            isDisabled={layouts.length === 0}
                                            isIndeterminate={publishIds.length > 0 && publishIds.length < layouts.length}
                                            onChange={(e) => setPublishIds(e.target.checked ? layouts.map((l) => l.id) : [])}
                                        >
                                            <Text fontSize="2xs">{t('dialogs.ui.plot.selectAll')}</Text>
                                        </Checkbox>
                                    </HStack>
                                    <VStack align="stretch" spacing={1} maxH="180px" overflowY="auto" borderWidth="1px" borderColor="border.base" borderRadius="md" p={2}>
                                        {layouts.length === 0 && (
                                            <Text fontSize="xs" color="text.secondary">{t('dialogs.ui.plot.noLayouts')}</Text>
                                        )}
                                        {layouts.map((l) => {
                                            const saved = layoutPageSetup(l);
                                            return (
                                                <Checkbox
                                                    key={l.id}
                                                    size="sm"
                                                    isChecked={publishIds.includes(l.id)}
                                                    onChange={(e) => setPublishIds((ids) => e.target.checked
                                                        ? [...ids, l.id]
                                                        : ids.filter((x) => x !== l.id))}
                                                >
                                                    <Text fontSize="xs">
                                                        {`${l.name} — ${saved.paperName}`}
                                                    </Text>
                                                </Checkbox>
                                            );
                                        })}
                                    </VStack>
                                </>
                            )}
                            <Text fontSize="2xs" color="text.secondary" mt={1}>
                                {publishSource === 'sheetSet'
                                    ? t('dialogs.ui.plot.sheetSetHint')
                                    : t('dialogs.ui.plot.publishHint')}
                            </Text>
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel fontSize="xs">{t('dialogs.ui.plot.fileName')}</FormLabel>
                            <Input
                                size="sm"
                                value={fileName}
                                onChange={(e) => setFileName(e.target.value)}
                                placeholder="publish.pdf"
                            />
                        </FormControl>
                    </>
                ) : (
                    <>
                        <SimpleGrid columns={2} spacing={3}>
                            <FormControl>
                                <FormLabel fontSize="xs">{t('dialogs.ui.plot.plotArea')}</FormLabel>
                                <Select
                                    size="sm"
                                    value={setup.plotType}
                                    isDisabled={!!activeLayout}
                                    onChange={(e) => patch({ plotType: e.target.value as PageSetup['plotType'] })}
                                >
                                    {activeLayout && <option value="layout">{t('dialogs.ui.plot.areaLayout')}</option>}
                                    <option value="extents">{t('dialogs.ui.plot.areaExtents')}</option>
                                    <option value="display">{t('dialogs.ui.plot.areaDisplay')}</option>
                                    <option value="window">{t('dialogs.ui.plot.areaWindow')}</option>
                                </Select>
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="xs">{t('dialogs.ui.plot.paper')}</FormLabel>
                                <Select
                                    size="sm"
                                    value={paperId}
                                    onChange={(e) => {
                                        const mm = paperSizeMm(e.target.value);
                                        const landscape = setup.paperWidth >= setup.paperHeight;
                                        patch({
                                            paperName: `${PAPER_SIZES[e.target.value]?.label ?? e.target.value} (${Math.max(mm.w, mm.h).toFixed(2)} x ${Math.min(mm.w, mm.h).toFixed(2)} mm)`,
                                            paperWidth: landscape ? mm.w : mm.h,
                                            paperHeight: landscape ? mm.h : mm.w,
                                        });
                                    }}
                                >
                                    {Object.entries(PAPER_SIZES).map(([id, size]) => (
                                        <option key={id} value={id}>{size.label}</option>
                                    ))}
                                </Select>
                            </FormControl>
                        </SimpleGrid>

                        <HStack spacing={4}>
                            <Checkbox
                                size="sm"
                                isChecked={setup.paperWidth >= setup.paperHeight}
                                onChange={(e) => {
                                    const landscape = e.target.checked;
                                    const w = Math.max(setup.paperWidth, setup.paperHeight);
                                    const h = Math.min(setup.paperWidth, setup.paperHeight);
                                    patch({ paperWidth: landscape ? w : h, paperHeight: landscape ? h : w });
                                }}
                            >
                                <Text fontSize="xs">{t('dialogs.ui.plot.landscape')}</Text>
                            </Checkbox>
                            <Checkbox
                                size="sm"
                                isChecked={setup.rotation === 180 || setup.rotation === 90}
                                onChange={(e) => {
                                    const base = (setup.paperWidth >= setup.paperHeight) ? 0 : 90;
                                    patch({ rotation: e.target.checked ? ((base === 0 ? 180 : 270) as PageSetup['rotation']) : (base as PageSetup['rotation']) });
                                }}
                            >
                                <Text fontSize="xs">{t('dialogs.ui.plot.upsideDown')}</Text>
                            </Checkbox>
                        </HStack>

                        <SimpleGrid columns={3} spacing={3}>
                            <FormControl>
                                <FormLabel fontSize="xs">{t('dialogs.ui.plot.scale')}</FormLabel>
                                <Select
                                    size="sm"
                                    value={setup.fitToPaper ? 'fit' : `${setup.scaleNum}/${setup.scaleDen}`}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        if (v === 'fit') { patch({ fitToPaper: true }); return; }
                                        const [n, d] = v.split('/').map(Number);
                                        patch({ fitToPaper: false, scaleNum: n, scaleDen: d });
                                    }}
                                >
                                    <option value="fit">{t('dialogs.ui.plot.fitToPaper')}</option>
                                    {STANDARD_SCALES.map((s) => (
                                        <option key={`${s.num}/${s.den}`} value={`${s.num}/${s.den}`}>
                                            {`${s.num}:${s.den}`}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="xs">{t('dialogs.ui.plot.plotStyle')}</FormLabel>
                                <Select
                                    size="sm"
                                    value={setup.plotStyleName}
                                    onChange={(e) => patch({ plotStyleName: e.target.value })}
                                >
                                    {styleNames.map((name) => (
                                        <option key={name || 'none'} value={name}>
                                            {name === '' ? t('dialogs.ui.plot.styleNone') : name}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="xs">{t('dialogs.ui.plot.importCtb')}</FormLabel>
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    w="full"
                                    onClick={() => document.getElementById('plot-ctb-input')?.click()}
                                >
                                    {t('dialogs.ui.plot.importCtbButton')}
                                </Button>
                                <input
                                    id="plot-ctb-input"
                                    type="file"
                                    accept=".ctb,.stb"
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) void importCtb(file);
                                        e.target.value = '';
                                    }}
                                />
                            </FormControl>
                        </SimpleGrid>

                        {setup.plotType === 'window' && (
                            <SimpleGrid columns={4} spacing={2}>
                                {(['x1', 'y1', 'x2', 'y2'] as const).map((key) => (
                                    <FormControl key={key}>
                                        <FormLabel fontSize="2xs">{t(`dialogs.ui.plot.window.${key}`)}</FormLabel>
                                        <NumberInput
                                            size="sm"
                                            value={setup.plotWindow?.[key] ?? 0}
                                            onChange={(_, v) => patch({
                                                plotWindow: {
                                                    x1: 0, y1: 0, x2: 0, y2: 0,
                                                    ...(setup.plotWindow ?? {}),
                                                    [key]: Number.isFinite(v) ? v : 0,
                                                },
                                            })}
                                        >
                                            <NumberInputField />
                                        </NumberInput>
                                    </FormControl>
                                ))}
                            </SimpleGrid>
                        )}
                    </>
                )}

                {!publishMode && (
                    <HStack spacing={4}>
                        <Checkbox
                            size="sm"
                            isChecked={setup.centerPlot}
                            onChange={(e) => patch({ centerPlot: e.target.checked })}
                        >
                            <Text fontSize="xs">{t('dialogs.ui.plot.centerPlot')}</Text>
                        </Checkbox>
                        {!setup.centerPlot && (
                            <>
                                <HStack spacing={1}>
                                    <Text fontSize="2xs" color="text.secondary">X</Text>
                                    <NumberInput size="sm" w="70px" value={setup.offsetX}
                                        onChange={(_, v) => patch({ offsetX: Number.isFinite(v) ? v : 0 })}>
                                        <NumberInputField />
                                    </NumberInput>
                                </HStack>
                                <HStack spacing={1}>
                                    <Text fontSize="2xs" color="text.secondary">Y</Text>
                                    <NumberInput size="sm" w="70px" value={setup.offsetY}
                                        onChange={(_, v) => patch({ offsetY: Number.isFinite(v) ? v : 0 })}>
                                        <NumberInputField />
                                    </NumberInput>
                                </HStack>
                            </>
                        )}
                    </HStack>
                )}

                {!publishMode && (
                    <SimpleGrid columns={2} spacing={2}>
                        <Checkbox size="sm" isChecked={setup.options.objectLineweights}
                            onChange={(e) => patch({ options: { ...setup.options, objectLineweights: e.target.checked } })}>
                            <Text fontSize="xs">{t('dialogs.ui.plot.objectLineweights')}</Text>
                        </Checkbox>
                        <Checkbox size="sm" isChecked={setup.options.scaleLineweights}
                            onChange={(e) => patch({ options: { ...setup.options, scaleLineweights: e.target.checked } })}>
                            <Text fontSize="xs">{t('dialogs.ui.plot.scaleLineweights')}</Text>
                        </Checkbox>
                        <Checkbox size="sm" isChecked={setup.options.plotTransparency}
                            onChange={(e) => patch({ options: { ...setup.options, plotTransparency: e.target.checked } })}>
                            <Text fontSize="xs">{t('dialogs.ui.plot.plotTransparency')}</Text>
                        </Checkbox>
                        <Checkbox size="sm" isChecked={setup.options.mergeLines}
                            onChange={(e) => patch({ options: { ...setup.options, mergeLines: e.target.checked } })}>
                            <Text fontSize="xs">{t('dialogs.ui.plot.mergeLines')}</Text>
                        </Checkbox>
                        <Checkbox size="sm" isChecked={setup.options.plotStamp}
                            onChange={(e) => patch({ options: { ...setup.options, plotStamp: e.target.checked } })}>
                            <Text fontSize="xs">{t('dialogs.ui.plot.plotStamp')}</Text>
                        </Checkbox>
                        {activeLayout && (
                            <Checkbox size="sm" isChecked={saveToLayout}
                                onChange={(e) => setSaveToLayout(e.target.checked)}>
                                <Text fontSize="xs">{t('dialogs.ui.plot.saveSetupToLayout')}</Text>
                            </Checkbox>
                        )}
                    </SimpleGrid>
                )}

                {!publishMode && (
                    <FormControl isRequired>
                        <FormLabel fontSize="xs">{t('dialogs.ui.plot.fileName')}</FormLabel>
                        <Input
                            size="sm"
                            value={fileName}
                            onChange={(e) => setFileName(e.target.value)}
                            placeholder="plot.pdf"
                        />
                    </FormControl>
                )}

                {styleWarnings.length > 0 && (
                    <Alert status="warning" borderRadius="md" fontSize="xs" py={2}>
                        <AlertIcon />
                        <Box>
                            {styleWarnings.map((w, i) => (
                                <Text key={i} fontSize="2xs">{w}</Text>
                            ))}
                        </Box>
                    </Alert>
                )}
                <Box minH="1" />
            </VStack>
        </AppDialog>
    );
}

/** Lazy-mount gate shared with ProjectDialogsHost. */
export function isPlotDialogEvent(e: Event): e is CustomEvent<PlotDialogRequest> {
    return e.type === 'yap:open-plot-dialog';
}
